import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Location } from '@angular/common';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AUTH_PRODUCT_ID, AUTH_SERVER_URL, INVALID_LOGIN_TOKEN_MESSAGE } from './auth.model';

const ACCESS_TOKEN_EXPIRY_STORAGE_KEY = 'access-token-expiry';
const REFRESH_TOKEN_STORAGE_KEY = 'refresh-token';
const REFRESH_TOKEN_EXPIRY_STORAGE_KEY = 'refresh-token-expiry';
const USER_ID_STORAGE_KEY = 'user-id';
const LAST_LOGGED_IN_STORAGE_KEY = 'last-logged-in';
const EXPIRY_REFRESH_WINDOW_MS = 60000;

interface AuthTokenResponse {
  token?: string;
  tokenExpiry?: string;
  refreshToken?: string;
  refreshTokenExpiry?: string;
  lastLoggedIn?: string;
}

@Injectable()
export class TokenInterceptorService implements HttpInterceptor {
  private readonly authEndpointProductCode: string;

  constructor(
    private location: Location,
    private injector: Injector,
    @Inject(AUTH_SERVER_URL) private authServerUrl: string,
    @Inject(AUTH_PRODUCT_ID) private productCode: string

  ) {
    this.authEndpointProductCode = productCode;

    const currentUrl = this.location.path();
    if (!currentUrl.includes('admin')) {
      const urlSegments = currentUrl.split('/');
      if (urlSegments.length > 1)
        this.productCode = (currentUrl.split('/')[1]).toUpperCase();
    }
  }


  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // Do not add the app access token to the auth server exchange/refresh calls
    // or to non-API requests. Auth failures on protected API/GraphQL calls are
    // handled below by refreshing once and retrying the original request.
    if (this.isAuthServerRequest(req.url) || (req.url.indexOf('api') < 0 && req.url.indexOf('graphql') < 0)) {
      return next.handle(req);
    }

    const authRequest = (async () => {
      while (!!(new URLSearchParams(window.location.search).get('token'))) {
        // Fixes some early request before url param is read.
        // Wait for AuthService to pickup the token and remove it from the window.location before continuing.
        await (new Promise(resolve => setTimeout(resolve, 500)));
      }

      if (req.headers.has('Authorization')) {
        return req;
      }

      const token = await this.ensureAccessToken();
      return this.withAccessToken(req, token);
    })();

    return from(authRequest)
      .pipe(switchMap(q => next.handle(q)))
      .pipe(
        catchError((errorResponse: any) => this.handleAuthError(errorResponse, req, next))
      );
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private handleAuthError(errorResponse: any, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isInvalidLoginTokenError(errorResponse)) {
      this.authService.goToLogin(window.location.pathname);
      return of(errorResponse);
    }

    if (!this.isAuthFailure(errorResponse)) {
      return throwError(errorResponse);
    }

    return from(this.refreshAccessToken()).pipe(
      switchMap(accessToken => {
        if (!accessToken) {
          this.goToLogin();
          return throwError(errorResponse);
        }

        const retryRequest = this.withAccessToken(req, accessToken);
        return next.handle(retryRequest);
      }),
      catchError(refreshOrRetryError => {
        if (this.isBrowserAuthFailure(refreshOrRetryError)) {
          this.goToLogin();
        }
        return throwError(refreshOrRetryError);
      })
    );
  }

  private withAccessToken(req: HttpRequest<any>, accessToken: string | null): HttpRequest<any> {
    if (!accessToken) {
      return req;
    }

    return req.clone({
      headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
    });
  }

  private async ensureAccessToken(forceRefresh: boolean = false): Promise<string | null> {
    const authService = this.authService as any;
    if (typeof authService?.ensureAccessToken === 'function') {
      return authService.ensureAccessToken(forceRefresh);
    }

    const accessToken = typeof authService?.getAccessTokenSnapshot === 'function'
      ? authService.getAccessTokenSnapshot()
      : null;
    if (!forceRefresh && accessToken && !this.isStoredExpiryNear(ACCESS_TOKEN_EXPIRY_STORAGE_KEY, EXPIRY_REFRESH_WINDOW_MS)) {
      return accessToken;
    }

    const refreshedAccessToken = await this.refreshAccessToken();
    return refreshedAccessToken || accessToken;
  }

  private async refreshAccessToken(): Promise<string | null> {
    const authService = this.authService as any;
    if (typeof authService?.refreshAccessToken === 'function') {
      return authService.refreshAccessToken();
    }

    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    const userId = localStorage.getItem(USER_ID_STORAGE_KEY);
    if (!refreshToken || !userId || this.isStoredExpiryNear(REFRESH_TOKEN_EXPIRY_STORAGE_KEY, 0)) {
      return null;
    }

    try {
      const response = await fetch(this.getAuthEndpoint(`api/${this.authEndpointProductCode}/refresh`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json() as AuthTokenResponse;
      return this.storeAuthResult(result, userId);
    } catch (error) {
      console.error('Failed to refresh application access token.', error);
      this.clearStoredAuth();
      return null;
    }
  }

  private storeAuthResult(result: AuthTokenResponse, userId: string): string | null {
    const accessToken = result?.token;
    if (!accessToken) {
      return null;
    }

    localStorage.setItem(USER_ID_STORAGE_KEY, userId);

    if (result.tokenExpiry) {
      localStorage.setItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY, result.tokenExpiry);
    } else {
      localStorage.removeItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY);
    }

    if (result.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, result.refreshToken);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }

    if (result.refreshTokenExpiry) {
      localStorage.setItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY, result.refreshTokenExpiry);
    } else {
      localStorage.removeItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY);
    }

    if (result.lastLoggedIn) {
      localStorage.setItem(LAST_LOGGED_IN_STORAGE_KEY, result.lastLoggedIn);
    }

    return accessToken;
  }

  private isStoredExpiryNear(storageKey: string, refreshWindowMs: number): boolean {
    const expiry = localStorage.getItem(storageKey);
    if (!expiry) {
      return false;
    }

    const expiryTime = Date.parse(expiry);
    if (Number.isNaN(expiryTime)) {
      return false;
    }

    return expiryTime <= Date.now() + refreshWindowMs;
  }

  private clearStoredAuth(): void {
    localStorage.removeItem('access-token');
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY);
    localStorage.removeItem(LAST_LOGGED_IN_STORAGE_KEY);
    localStorage.removeItem(USER_ID_STORAGE_KEY);
  }

  private goToLogin(): void {
    const authService = this.authService as any;
    if (typeof authService?.goToLogin === 'function') {
      authService.goToLogin(window.location.pathname);
    }
  }

  private isAuthFailure(errorResponse: any): boolean {
    return this.isBrowserAuthFailure(errorResponse) || this.isWrappedBackendAuthFailure(errorResponse);
  }

  private isBrowserAuthFailure(errorResponse: any): boolean {
    return errorResponse instanceof HttpErrorResponse && (errorResponse.status === 401 || errorResponse.status === 403);
  }

  private isWrappedBackendAuthFailure(errorResponse: any): boolean {
    if (!(errorResponse instanceof HttpErrorResponse) || errorResponse.status !== 500) {
      return false;
    }

    const errorText = typeof errorResponse.error === 'string'
      ? errorResponse.error
      : JSON.stringify(errorResponse.error || {});

    return errorText.includes('User not authenticated')
      || errorText.includes('User token is expired')
      || errorText.includes('Invalid user token');
  }

  private isInvalidLoginTokenError(errorResponse: any): boolean {
    return errorResponse instanceof HttpErrorResponse
      && errorResponse.status === 400
      && errorResponse.error?.message === INVALID_LOGIN_TOKEN_MESSAGE;
  }

  private isAuthServerRequest(url: string): boolean {
    const authServerUrl = this.authServerUrl.endsWith('/') ? this.authServerUrl : `${this.authServerUrl}/`;
    return url.startsWith(authServerUrl);
  }

  private getAuthEndpoint(path: string): string {
    const authServerUrl = this.authServerUrl.endsWith('/') ? this.authServerUrl : `${this.authServerUrl}/`;
    return `${authServerUrl}${path.replace(/^\//, '')}`;
  }

}
